import configuration from './config.json';
import { localTime, formatStartOf } from '@helpers';
import { MomentInput, DurationInputArg2 } from 'moment';

// min,max - minute
// primary,secondary - seconds

interface Interval {
  primary: number;
  secondary: number;
  min: number;
  max: number;
  primaryFormat: string;
  secondaryFormat: string;
}

export interface LegendRules {
  bigLineDistance: number;
  smallLineDistance: number;
  amountOfAdditionalSmallLine: number;
  amountOfInitialSmallLine: number;
  initialSmallLineOffsetSeconds: number;
  initialOffsetSeconds: number;
  amountOfBigLine: number;
  amountOfSmallLine: number;
  secondWidth: number;
  isZoomInDisabled: boolean;
  isZoomOutDisabled: boolean;
  isZoomResetDisabled: boolean;
  interval: Interval;
}

export const legendRules = ({
  startTime,
  endTime,
  blockWidth,
  selectedTime,
}: {
  startTime: MomentInput;
  endTime: MomentInput;
  blockWidth: number;
  selectedTime: DurationInputArg2;
}): LegendRules => {
  const amountOfDays = localTime(endTime).diff(localTime(startTime), 'd');
  const amountOfSeconds = localTime(endTime).diff(localTime(startTime), 's') + 1;
  const amountOfMinutes = Math.round(amountOfSeconds / 60);

  const secondWidth = blockWidth / amountOfSeconds;

  const minimumBigLineDistance = 72;

  const DP = blockWidth / amountOfMinutes;
  const minimumInterval = Math.ceil(minimumBigLineDistance / DP);
  const interval = configuration.find((i) => i.min <= minimumInterval && i.max > minimumInterval) || configuration[configuration.length - 1];

  const bigLineDistance = interval.primary * secondWidth;
  const smallLineDistance = interval.secondary * secondWidth;
  const amountOfSmallLine = Math.floor(bigLineDistance / smallLineDistance);
  
  const moduloPrimary = localTime(startTime).diff(localTime(startTime).startOf('day'), 's') % interval.primary;
  const initialOffsetSeconds = moduloPrimary ? -moduloPrimary + interval.primary : 0;
  const initialSmallLineOffsetSeconds = initialOffsetSeconds % interval.secondary;
  const amountOfInitialSmallLine = Math.floor(initialOffsetSeconds / interval.secondary);

  const amountOfBigLine = Math.floor((blockWidth - initialOffsetSeconds * secondWidth) / bigLineDistance);
  
  const overSpacePX = blockWidth - (amountOfBigLine * bigLineDistance + initialOffsetSeconds * secondWidth);
  const amountOfAdditionalSmallLine = Math.floor(overSpacePX / smallLineDistance);

  const periodDurationByDefault = localTime(endTime)
    .subtract(1, 's')
    .endOf(formatStartOf(selectedTime))
    .add(1, 's')
    .diff(localTime(startTime).startOf(formatStartOf(selectedTime)), 's');

  return {
    bigLineDistance,
    smallLineDistance,
    amountOfInitialSmallLine,
    amountOfAdditionalSmallLine,
    initialSmallLineOffsetSeconds,
    initialOffsetSeconds,
    amountOfBigLine,
    amountOfSmallLine,
    interval,
    secondWidth,
    isZoomInDisabled: amountOfSeconds < 119,
    isZoomOutDisabled: amountOfDays > 2,
    isZoomResetDisabled: Math.abs(amountOfSeconds - periodDurationByDefault) < 30,
  };
};
